/**
 * SharedItemView.tsx
 *
 * Option 4 — Magic Link read-only view for external stakeholders.
 *
 * Reached via:  /shared/:token
 * Token is generated in reminderCron when building external owner emails.
 * No login required. Displays safe, limited item data only.
 */

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";

interface SharedItem {
  name: string;
  category: string;
  expiry_date: string;
  days_left: number;
  status_text: string;
  status_color: string;
  status_bg: string;
  quantity: string | null;
  creator_name: string;
  company_name: string | null;
  recipient: string;
  app_name: string;
  expires_at: string;
}

type State =
  | { phase: "loading" }
  | { phase: "ok"; data: SharedItem }
  | { phase: "error"; message: string };

export default function SharedItemView() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<State>({ phase: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ phase: "error", message: "No token provided." });
      return;
    }

    fetch(`/api/shared/view/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || "Link not found");
        return json as SharedItem;
      })
      .then((data) => setState({ phase: "ok", data }))
      .catch((err: Error) =>
        setState({ phase: "error", message: err.message })
      );
  }, [token]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (state.phase === "loading") {
    return (
      <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-[#6B7280]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2563EB]" />
          <p className="text-sm font-medium">Loading document…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (state.phase === "error") {
    return (
      <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-[#2563EB] px-8 py-6">
            <div className="text-white font-bold text-lg">📄 Sentrilog</div>
          </div>
          {/* Body */}
          <div className="px-8 py-10 text-center">
            <div className="text-5xl mb-4">🔗</div>
            <h1 className="text-xl font-bold text-[#111827] mb-3">
              Link Not Available
            </h1>
            <p className="text-[#6B7280] text-sm mb-6 leading-relaxed">
              {state.message}
            </p>
            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-xl p-4 text-left">
              <p className="text-sm text-[#DC2626] font-medium">
                This link may have expired or been revoked. Please contact the
                person who sent you this reminder.
              </p>
            </div>
          </div>
          {/* Footer */}
          <div className="bg-[#F9FAFB] border-t border-[#E5E7EB] px-8 py-4 text-center">
            <p className="text-xs text-[#9CA3AF]">Powered by Sentrilog</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────
  const { data } = state;
  const isExpired = data.days_left < 0;
  const isUrgent = data.days_left >= 0 && data.days_left <= 7;

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex flex-col items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* ── Brand header ──────────────────────────────────────────────── */}
        <div className="bg-[#2563EB] px-8 py-6">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.2)" }}
            >
              <span className="text-white text-lg">📄</span>
            </div>
            <div>
              <div className="text-white font-bold text-lg leading-tight">
                {data.app_name}
              </div>
              <div className="text-blue-200 text-xs">
                Document Expiry Reminder
              </div>
            </div>
          </div>
        </div>

        {/* ── Main content ─────────────────────────────────────────────── */}
        <div className="px-8 py-8">
          <p className="text-sm text-[#6B7280] mb-6">
            <strong>{data.creator_name}</strong>
            {data.company_name ? ` from ${data.company_name}` : ""} has shared
            this document expiry notice with you.
          </p>

          {/* Document card */}
          <div className="border border-[#E5E7EB] rounded-xl overflow-hidden mb-6">
            {/* Status strip */}
            <div
              className="px-5 py-3 flex items-center justify-between"
              style={{ background: data.status_bg }}
            >
              <span
                className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: data.status_color }}
              >
                {isExpired
                  ? "⚠️ Expired"
                  : isUrgent
                    ? "⚡ Urgent"
                    : "📋 Reminder"}
              </span>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold"
                style={{
                  background: data.status_bg,
                  color: data.status_color,
                  border: `1px solid ${data.status_color}33`,
                }}
              >
                {data.status_text}
              </span>
            </div>

            {/* Document details */}
            <div className="px-5 py-5 space-y-4">
              <div>
                <div className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">
                  Document
                </div>
                <div className="text-lg font-bold text-[#111827]">
                  {data.name}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">
                    Category
                  </div>
                  <div className="text-sm font-medium text-[#374151]">
                    {data.category}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">
                    Expiry Date
                  </div>
                  <div className="text-sm font-medium text-[#374151]">
                    {data.expiry_date}
                  </div>
                </div>

                {data.quantity && (
                  <div>
                    <div className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">
                      Quantity
                    </div>
                    <div className="text-sm font-medium text-[#374151]">
                      {data.quantity}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info note */}
          <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl p-4 text-sm text-[#0369A1] leading-relaxed">
            <strong>What should you do?</strong>
            <br />
            {isExpired
              ? "This document has already expired. Please contact the sender as soon as possible."
              : isUrgent
                ? "This document expires very soon. Please take action or contact the sender."
                : "Please review this document and take any necessary renewal or replacement actions before it expires."}
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div className="bg-[#F9FAFB] border-t border-[#E5E7EB] px-8 py-4 flex items-center justify-between">
          <p className="text-xs text-[#9CA3AF]">
            Sent to{" "}
            <span className="font-medium text-[#6B7280]">{data.recipient}</span>
          </p>
          <Link
            to="/"
            className="text-xs text-[#2563EB] hover:underline font-medium"
          >
            About {data.app_name}
          </Link>
        </div>
      </div>

      {/* Link expiry note */}
      <p className="mt-4 text-xs text-[#9CA3AF] text-center">
        This link expires on{" "}
        {new Date(data.expires_at).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })}
        . After that it will no longer be accessible.
      </p>
    </div>
  );
}
